import {
    ApiShopperLandingPageLink,
    VLPVariant,
} from '@storefronts/common-types'
import type { LandingPageModel } from '../models/landingPageModel'
import { nanoid } from 'nanoid'
import { Cart, VariantWithImage } from './types'
import { parseCookie, transformLinkToVariant } from './utils/helpers'
import { isStorageAndTrackingAllowed } from './components/consent/consentTrackingStore'

// NEVER CHANGE THIS IT IS IN USE BY HELLA CONVERSION PIXELS
const VIDDY_CART = '__vd_cart__'
const VIDDY_ID = '__vd_service_user_id__'
// Recently Viewed Products Storage
const BLACK_CROW_RECENTLY_VIEWED = '__bc_recently_viewed__'

const ONE_HOUR = 60 * 60
const ONE_DAY = ONE_HOUR * 24
const ONE_MONTH = ONE_DAY * 31
const DEFAULT_EXPIRY = ONE_MONTH * 3

// Storage access throws in sandboxed documents such as the admin preview.
export const localAndCookieKeys = (key: string) => {
    let localStorageValue: string | null = null
    try {
        localStorageValue = window.localStorage.getItem(key)
    } catch (e) {
        console.log(e)
    }
    let cookies: Record<string, string> = {}
    try {
        cookies = parseCookie(document.cookie)
    } catch (e) {
        console.log(e)
    }

    const cookieValue = key in cookies ? cookies[key] : null
    return [localStorageValue, cookieValue]
}

const getFromStorage = (key: string): string | null => {
    try {
        const [localObject, cookieObject] = localAndCookieKeys(key)
        // This order is important since cookies persist across subdomains/domains
        if (cookieObject) {
            return cookieObject
        }
        if (localObject) {
            return localObject
        }
        return null
    } catch (e) {
        console.log(e)
        return null
    }
}

type StoredCart = {
    items: {
        variantId: string
        quantity: number
        additionalVariantInfo?: VariantWithImage
    }[]
    id: string
    checkoutUrl?: string
}

// TODO: Move this to a util?
const findVariantByIdInLandingPage = (
    variantId: string,
    landingPage: LandingPageModel,
) => {
    const link = [...landingPage.landingPageLinks, ...landingPage.cartUpsells]
        .map((link) => link.variantInfo ?? [])
        .flat()
    return link.find((variant) => variant.shopifyId === variantId)
}

export const setInStorage = (key: string, value: any, age = DEFAULT_EXPIRY) => {
    if (!isStorageAndTrackingAllowed()) return
    const valueString = encodeURIComponent(JSON.stringify(value))
    window.localStorage.setItem(key, valueString)
    document.cookie = `${key}=${valueString};domain=${getCookieDomain()};path=/;max-age=${age};`
}

export const getFromStorageParsed = (key: string) => {
    const valueString = getFromStorage(key)
    if (!valueString) return null
    return JSON.parse(decodeURIComponent(valueString))
}

export const getCartFromStorage = (
    landingPage: LandingPageModel,
): Cart | null => {
    try {
        const cartStr = getFromStorage(
            `${VIDDY_CART}_${landingPage.landingPageHash}`,
        )
        if (cartStr) {
            const linkIdLinkMap: Record<string, VariantWithImage> = {}
            landingPage.landingPageLinks.forEach(
                (link: ApiShopperLandingPageLink) => {
                    linkIdLinkMap[link.shopifyId] = transformLinkToVariant(link)
                    if (link.variantInfo) {
                        link.variantInfo.forEach((variant: VLPVariant) => {
                            linkIdLinkMap[variant.shopifyId] = {
                                ...variant,
                                imageUrl:
                                    variant.featuredImage || link.imageUrl,
                                link,
                                finalPrice: variant.price.price,
                                isSub: false,
                            }
                        })
                    }
                },
            )
            landingPage.cartUpsells.forEach(
                (link: ApiShopperLandingPageLink) => {
                    linkIdLinkMap[link.shopifyId] = transformLinkToVariant(link)
                    if (link.variantInfo) {
                        link.variantInfo.forEach((variant: VLPVariant) => {
                            linkIdLinkMap[variant.shopifyId] = {
                                ...variant,
                                imageUrl:
                                    variant.featuredImage || link.imageUrl,
                                link,
                                finalPrice: variant.price.price,
                                isSub: false,
                            }
                        })
                    }
                },
            )

            let storedCart: StoredCart | null

            try {
                storedCart = JSON.parse(cartStr) as StoredCart
            } catch (_e) {
                storedCart = JSON.parse(
                    decodeURIComponent(cartStr),
                ) as StoredCart
            }

            if (storedCart === null) {
                throw new Error('Cart is null')
            }

            const filteredItems = storedCart.items.filter((item) => {
                return (
                    !!item &&
                    (!!linkIdLinkMap[item.variantId] ||
                        !!item.additionalVariantInfo)
                )
            })

            const parsedItems = filteredItems.map((item: any) => {
                return {
                    quantity: item.quantity,
                    variant: {
                        ...(item.additionalVariantInfo ||
                            linkIdLinkMap[item.variantId]),
                        finalPrice: item.finalPrice,
                        isSub: item.isSub,
                    },
                }
            })

            return {
                items: parsedItems,
                id: storedCart.id,
                checkoutUrl: storedCart.checkoutUrl,
            }
        }
        return null
    } catch (e) {
        console.log(e)
        return null
    }
}

const getViddyIdFromStorage = (): string | null => {
    try {
        return getFromStorage(`${VIDDY_ID}`)
    } catch (e) {
        console.log(e)
        return null
    }
}

const getCookieDomain = () => {
    const domain = window.location.hostname
    if (domain === 'localhost') {
        return 'localhost'
    }
    const parts = domain.split('.')
    if (parts.length > 2) {
        return parts.slice(1).join('.')
    }
    return domain
}

let mintedThisTab: string | null = null

export const resetInitializedViddyId = (): void => {
    mintedThisTab = null
}

export const initializeViddyId = (): string => {
    if (mintedThisTab) return mintedThisTab

    const existingId = getViddyIdFromStorage()
    if (existingId) return existingId
    if (!isStorageAndTrackingAllowed()) return ''

    const id = nanoid()
    mintedThisTab = id
    try {
        window.localStorage.setItem(`${VIDDY_ID}`, id)
        document.cookie = `${VIDDY_ID}=${id};domain=${getCookieDomain()};path=/;max-age=31536000;`
    } catch (e) {
        // Private mode / quota should still return an in-memory id.
        console.log(e)
    }
    return id
}

export const setCartInStorage = (cart: Cart, landingPage: LandingPageModel) => {
    if (!isStorageAndTrackingAllowed()) return
    const storedItems = cart.items.map((item) => ({
        quantity: item.quantity,
        variantId: `${item.variant.shopifyId}`,
        finalPrice: item.variant.finalPrice,
        isSub: item.variant.isSub,
        additionalVariantInfo: !findVariantByIdInLandingPage(
            item.variant.shopifyId,
            landingPage,
        )
            ? {
                  ...item.variant,
                  // Minimizes the link object to only the necessary fields.
                  // in order to minimize the cart's size and avoid
                  // overflowing the cookie.
                  link: {
                      shopifyId: `${item.variant.link.shopifyId}`,
                      title: item.variant.link.title,
                      // Assumption, if there is an option, there are multiple variants
                      // need this to display the variant's title in the cart.
                      variantInfo: item.variant.option1 ? [{}, {}] : [],
                  } as ApiShopperLandingPageLink,
              }
            : undefined,
    }))

    const storedCart: StoredCart = {
        items: storedItems,
        id: cart.id,
        checkoutUrl: cart.checkoutUrl,
    }

    const cartString = encodeURIComponent(JSON.stringify(storedCart))

    try {
        window.localStorage.setItem(
            `${VIDDY_CART}_${landingPage.landingPageHash}`,
            cartString,
        )
        document.cookie = `${VIDDY_CART}_${landingPage.landingPageHash}=${cartString};domain=${getCookieDomain()};path=/;max-age=${ONE_HOUR};`
    } catch (e) {
        console.log(e)
    }
}

export const getCartTokenCookie = () => {
    return getFromStorage('cart')
}

export const setCartTokenCookie = (value: string) => {
    if (!isStorageAndTrackingAllowed()) return
    // Don't set the domain as Shopify shop doesn't set the domain.
    // Setting the domain causes the cookie to persist after after checking
    // out and breaks the Shopify online store cart functionality.
    document.cookie = `cart=${encodeURIComponent(value)};path=/;max-age=864000;`
}

// Store only the product identifier - full data is fetched from context
export type RecentlyViewedProduct = {
    linkHash: string
}

export const getRecentlyViewedFromSession = (
    landingPageHash: string,
): RecentlyViewedProduct[] => {
    try {
        const key = `${BLACK_CROW_RECENTLY_VIEWED}${landingPageHash}`
        const stored = window.sessionStorage.getItem(key)
        if (!stored) return []
        const parsed = JSON.parse(stored)
        if (!Array.isArray(parsed)) return []

        // Validate that each item has linkHash
        return parsed.filter((item) => item && item.linkHash)
    } catch (e) {
        console.log(e)
        return []
    }
}

// Cache keyed by the raw sessionStorage string so `useSyncExternalStore`
// gets a referentially stable snapshot when nothing has changed (required to
// avoid an infinite re-render loop - see useShopPayButtons.ts for the pattern).
let recentlyViewedSnapshotCache: {
    key: string
    raw: string | null
    value: RecentlyViewedProduct[]
} | null = null

export const getRecentlyViewedSnapshot = (
    landingPageHash: string,
): RecentlyViewedProduct[] => {
    if (typeof window === 'undefined') return []

    const key = `${BLACK_CROW_RECENTLY_VIEWED}${landingPageHash}`
    let raw: string | null = null
    try {
        raw = window.sessionStorage.getItem(key)
    } catch (e) {
        console.log(e)
    }

    if (
        recentlyViewedSnapshotCache?.key === key &&
        recentlyViewedSnapshotCache.raw === raw
    ) {
        return recentlyViewedSnapshotCache.value
    }

    const value = getRecentlyViewedFromSession(landingPageHash)
    recentlyViewedSnapshotCache = { key, raw, value }
    return value
}

export const addRecentlyViewedToSession = (
    landingPageHash: string,
    productLink: ApiShopperLandingPageLink,
) => {
    try {
        const existing = getRecentlyViewedFromSession(landingPageHash)

        // Remove duplicates and add to front
        const filtered = existing.filter(
            (p) => p.linkHash !== productLink.linkHash,
        )
        const updated = [{ linkHash: productLink.linkHash }, ...filtered]

        const key = `${BLACK_CROW_RECENTLY_VIEWED}${landingPageHash}`
        window.sessionStorage.setItem(key, JSON.stringify(updated))
    } catch (e) {
        console.log('Error adding to recently viewed:', e)
    }
}
